import React, { FC, ReactNode, useMemo } from 'react';
import { Field, FieldRenderProps, useForm } from 'react-final-form';
import { forms } from '@factorin/container.macro';
import { FormState } from 'final-form';

import { Box, Button2, CalendarInputField, Stack, Typography } from 'Modules/ui3';
import { validators } from 'Modules/core/forms';
import { useTranslation } from 'Modules/core/i18n';

import { getRangeButtonsConfigs, RangeButtonsTypes } from './utils';


export { RangeButtonsTypes, getRangeButtonsConfigs } from './utils';


const PERIOD_PLACEHOLDER = '__.__.____';


interface ModifiedHandlers {
    onModifiedChange: (onChange: FieldRenderProps<Date>['input']['onChange']) => (date: Date) => void;
}

export interface PeriodFieldsProps<T> {
    errors: FormState<T>['errors'];
    periodName?: string;
    filterNames: [string, string];
    individualLabels?: {
        labelFrom?: ReactNode;
        labelTo?: ReactNode;
    };
    dashOff?: boolean;
    required?: boolean;
    isUTCConvert?: boolean;
    modifiedHandlers?: {
        from?: ModifiedHandlers;
        to?: ModifiedHandlers;
    };
    fastPeriodButtons?: RangeButtonsTypes[];
}


const Line: FC = () => (
    <Box height='100%'>
        <Box
            mt={12.25}
            alignSelf='center'
            width={({ spacing }) => spacing(5)}
            sx={{
                borderTopStyle: 'solid',
                borderTopWidth: '1px',
                borderTopColor: 'grayscale.shade40',
            }}
        />
    </Box>
);


export const PeriodFields = <T extends Record<string, any>>({
    periodName, filterNames, errors, individualLabels, dashOff = false,
    required = false, isUTCConvert = false, modifiedHandlers, fastPeriodButtons,
}: PeriodFieldsProps<T>) => {
    const { t } = useTranslation('buttonsPeriods');

    const { change } = useForm();

    const isFastPeriodButtonsExists = Boolean(fastPeriodButtons?.length);

    const rangeButtonsConfig = useMemo(() => getRangeButtonsConfigs(filterNames, change, t), [
        filterNames, change, t,
    ]);

    return (
        <Stack position='relative'>
            <Stack
                direction='row'
                spacing={5}
            >
                <Stack spacing={2} width='100%'>
                    {individualLabels?.labelFrom && (
                        <Typography
                            color='grayscale.shade85'
                            variant='regularCaption1'
                        >
                            {individualLabels.labelFrom}
                        </Typography>
                    )}

                    <Field<Date>
                        name={filterNames[0]}
                        noLabel
                        needCloseSuffix
                        isUTCConvert={isUTCConvert}
                        placeholder={PERIOD_PLACEHOLDER}
                        popperProps={{
                            placement: 'bottom-start',
                        }}
                        intervalError={errors?.[`${periodName}IntervalErrors`]}
                        {...required && {
                            validate: validators.required,
                        }}
                    >
                        {({ input: { onChange, ...restInputProps }, ...restFieldProps }) => {
                            const changeHandler = modifiedHandlers?.from?.onModifiedChange(
                                onChange,
                            ) ?? onChange;

                            return (
                                <CalendarInputField
                                    {...restFieldProps}
                                    input={{
                                        ...restInputProps,
                                        onChange: changeHandler,
                                    }}
                                    onChange={changeHandler}
                                    value={restInputProps.value}

                                    isFastPeriodButtonsExists={isFastPeriodButtonsExists}
                                />
                            );
                        }}
                    </Field>
                </Stack>

                {!dashOff && <Line />}

                <Stack spacing={2} width='100%' {...!individualLabels?.labelTo && { pt: 7 }}>
                    {individualLabels?.labelTo && (
                        <Typography
                            color='grayscale.shade85'
                            variant='regularCaption1'
                        >
                            {individualLabels.labelTo}
                        </Typography>
                    )}

                    <Field<Date>
                        name={filterNames[1]}
                        noLabel
                        needCloseSuffix
                        isUTCConvert={isUTCConvert}
                        placeholder={PERIOD_PLACEHOLDER}
                        popperProps={{
                            placement: 'bottom-end',
                        }}
                        intervalError={errors?.[`${periodName}IntervalErrors`]}
                        {...required && {
                            validate: validators.required,
                        }}
                    >
                        {({ input: { onChange, ...restInputProps }, ...restFieldProps }) => {
                            const changeHandler = modifiedHandlers?.to?.onModifiedChange(
                                onChange,
                            ) ?? onChange;

                            return (
                                <CalendarInputField
                                    {...restFieldProps}
                                    input={{
                                        ...restInputProps,
                                        onChange: changeHandler,
                                    }}
                                    onChange={changeHandler}
                                    value={restInputProps.value}

                                    isFastPeriodButtonsExists={isFastPeriodButtonsExists}
                                />
                            );
                        }}
                    </Field>
                </Stack>
            </Stack>

            {errors?.[`${periodName}IntervalErrors`] && (
                <Box
                    mt={2}
                    {...isFastPeriodButtonsExists ? { bottom: 0 } : { top: 76 }}
                >
                    <Typography sx={{ color: 'infoCustom.red50' }} variant='regularCaption3'>
                        <forms.errorRenderer
                            error={{ key: errors.key, values: { meta: errors.meta } }}
                        />
                    </Typography>
                </Box>
            )}

            {isFastPeriodButtonsExists && (
                <Stack
                    spacing={2}
                    direction='row'
                >
                    {fastPeriodButtons.map(buttonType => (
                        <Button2
                            key={buttonType}
                            size='extraExtraSmall'
                            variant='stroke'
                            onClick={rangeButtonsConfig[buttonType].handler}
                        >
                            {rangeButtonsConfig[buttonType].name}
                        </Button2>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};
